import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface AccuracyReportParams {
  formatType: 'html' | 'pdf' | 'xls'; // required
  displayFor: string; // required  
  showDetails: boolean; // required
  accuracyReportType: 'LATEST_VERSION' | 'ORIGINAL_VERSION'; // required
  fromQuarter?: number;
  toQuarter?: number;
  fromYear?: number;
  toYear?: number;
  table?: string;
  designatedOperator?: string;
}

export interface AccuracyReportResponse {
  // For HTML format, contains the report data
  data?: any;
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

/**
 * Hook to generate accuracy reports
 * 
 * @returns Mutation object for generating accuracy reports
 */
export function useAccuracyReport() {
  return useMutation<AccuracyReportResponse, Error, AccuracyReportParams>({
    mutationFn: async (params: AccuracyReportParams) => {
      // Create URLSearchParams for accuracy report endpoint
      const searchParams = new URLSearchParams();
      
      // Add required parameters
      searchParams.append('formatType', params.formatType);
      searchParams.append('displayFor', params.displayFor);
      searchParams.append('showDetails', params.showDetails.toString());
      searchParams.append('accuracyReportType', params.accuracyReportType);
      
      // Add optional parameters
      if (params.fromQuarter !== undefined) {
        searchParams.append('fromQuarter', params.fromQuarter.toString());
      }
      
      if (params.toQuarter !== undefined) {
        searchParams.append('toQuarter', params.toQuarter.toString());
      }
      
      if (params.fromYear !== undefined) {
        searchParams.append('fromYear', params.fromYear.toString());
      }
      
      if (params.toYear !== undefined) {
        searchParams.append('toYear', params.toYear.toString());
      }
      
      if (params.table) {
        searchParams.append('table', params.table);
      }
      
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/accuracy?${searchParams.toString()}`, {
        // HTML returns JSON with report data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the report data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `Accuracy_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 